import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ApiSanctionAlert,
  ApiSanctionEntityType,
  ApiSanctionHit,
  ApiSanctionHitType,
  ApiSanctionType,
  SanctionHitsPage,
} from '../../../api';
import { sanctionHitApi } from '../../../apis';
import { Card, Collapse } from 'react-bootstrap';
import ShowHideButton from '../../../common/ShowHideButton';
import { Hits } from './helper';
import './Matches.scss';
import LoadMoreTable from '../../../table/LoadMoreTable';
import { InternalApiCustomListSchema } from '../../../api-internal';
import { getSchema } from '../../customListSchemaFieldActions';
import { fetchScreeningEvents } from '../../events/screeningEventActions';
import { useAlertStatuses } from '../../alert-status/alertStatusesHelper';
import { faSparkles } from '@fortawesome/pro-light-svg-icons';
import { SalvIconButton } from '@salvapp/ui-components';
import { useMe } from '../../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { saveAiInsightClickEvent } from '../../../tracking/userEventTracker';
import { scroller } from '../../../common/scroller';

interface MatchProps {
  sanctionAlertId: number;
  alert: ApiSanctionAlert;
  rfiHandle: string | undefined;
  initialActiveHits: SanctionHitsPage;
  initialFilteredHits: SanctionHitsPage;
  screenedValue?: string;
}

export const Matches = ({
  sanctionAlertId,
  alert,
  rfiHandle,
  initialActiveHits,
  initialFilteredHits,
  screenedValue = '',
}: MatchProps) => {
  const { features } = useMe();
  const [expanded, setExpanded] = useState(false);
  const size = 20;
  const page = 0;
  const [hasRfiScreeningEvent, setHasRfiScreeningEvent] = useState<boolean | undefined>(undefined);

  const [customListSchema, setCustomListSchema] = useState<InternalApiCustomListSchema>();
  const [, statuses] = useAlertStatuses();
  const isAlertGoodlistingOthers = useMemo(() => {
    const statusAppliesGoodlisting = statuses.find((it) => it.id === alert.statusId)?.isGoodlistable;
    return (
      alert.entity.entityType === ApiSanctionEntityType.TRANSACTION && statusAppliesGoodlisting && alert.isGoodlistable
    );
  }, [statuses, alert.statusId, alert.entity.entityType, alert.isGoodlistable]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (rfiHandle) {
        fetchScreeningEvents(rfiHandle)
          .then((result) => {
            setHasRfiScreeningEvent(result.length > 0);
          })
          .catch(console.error);
      }
    };
    fetchEvents();
  }, [rfiHandle]);

  const fetchFilteredHits = async (offset: number, size: number) => {
    const hits = await sanctionHitApi.findSanctionHits({
      page: offset,
      size,
      sanctionAlertId,
      type: ApiSanctionHitType.FILTERED,
    });
    return hits.content;
  };

  const fetchActiveHits = async (offset: number, size: number) => {
    const hits = await sanctionHitApi.findSanctionHits({
      page: offset,
      size,
      sanctionAlertId,
      type: ApiSanctionHitType.ACTIVE,
    });
    return hits.content;
  };

  const findSchema = useCallback(async (screeningListId: string) => {
    setCustomListSchema(await getSchema(screeningListId));
  }, []);

  useEffect(() => {
    if (alert.customListId) {
      findSchema(alert.customListId);
    }
  }, [alert.customListId]);

  interface FilteredSectionProps {
    initialFilteredHits: SanctionHitsPage;
  }
  const FilteredSection = ({ initialFilteredHits }: FilteredSectionProps) => {
    return (
      <Card>
        <Card.Header onClick={() => setExpanded(!expanded)} className='cursor-pointer'>
          <h4 className='card-header-title'>
            Filtered matches <span className='link-color'>({initialFilteredHits.total})</span>
          </h4>
          <ShowHideButton expanded={expanded} />
        </Card.Header>
        <Collapse in={expanded}>
          <LoadMoreTable<ApiSanctionHit>
            title='Filtered matches'
            subTitle={getSubTitle(initialFilteredHits.content, false, 'filtered')}
            className='matches-table mb-0 border-0'
            total={initialFilteredHits.total}
            page={page}
            size={size}
            initialItems={initialFilteredHits.content}
            body={(items) => (
              <Hits
                hits={items}
                entityType={alert.entity.entityType}
                matchSources={alert.matchSources!}
                schemaFields={customListSchema?.schemaFields ?? []}
                customListId={alert.type === ApiSanctionType.CUSTOM_SCREENING_LIST ? alert.customListId : undefined}
              />
            )}
            header={() => getHeader()}
            fetchPage={fetchFilteredHits}
          />
        </Collapse>
      </Card>
    );
  };

  const getHeader = () => {
    return (
      <tr>
        <th className='sanction-match-field'>MATCH</th>
        <th>SCORE</th>
        <th>SOURCE</th>
      </tr>
    );
  };

  const getSubTitle = (
    hits: ApiSanctionHit[],
    goodlisting?: boolean,
    informationString?: string
  ): string | undefined => {
    if (goodlisting) {
      return 'Matches are in the goodlist and can not trigger new alerts. To remove from the goodlist change the alert status.';
    }
    if (!hasRfiScreeningEvent) {
      return undefined;
    }
    let message = 'Re-screened after RFI response received.';
    const rfiHits = hits.filter((it) => !!it.matchSourceId);
    if (rfiHits.length === 1) {
      message += ` ${rfiHits.length} new ${informationString ? informationString + ' ' : ''}match found.`;
    } else {
      message += ` ${rfiHits.length} new ${informationString ? informationString + ' ' : ''}matches found.`;
    }
    return message;
  };

  const [isFirstMatchAiInsightExpanded, setFirstMatchAiInsightExpanded] = useState(false);
  const toggleFirstMatchAiInsightExpansion = () => setFirstMatchAiInsightExpanded((prev) => !prev);

  const SalvAiLink = () => (
    <SalvIconButton
      icon={faSparkles}
      variant={'primary'}
      ariaLabel={'Show AI Insight under the first Match'}
      onClick={async () => {
        await saveAiInsightClickEvent();
        setFirstMatchAiInsightExpanded(true);
        setTimeout(() => {
          scroller.scrollTo('salv-first-match', {
            smooth: true,
            duration: 400,
            offset: -200,
          });
        }, 100);
      }}
    />
  );

  return (
    <ScreeningMatchesContext.Provider
      value={{
        isFirstMatchAiInsightExpanded,
        toggleFirstMatchAiInsightExpansion,
      }}
    >
      {initialActiveHits.content.length > 0 && (
        <LoadMoreTable<ApiSanctionHit>
          title='Matches'
          titleActions={
            isFeatureEnabled(ORGANISATION_FEATURE.SHOW_AI_INSIGHTS_FOR_SCREENING_HITS, features) &&
            initialActiveHits.content.some((hit) => hit.aiInsight) ? (
              <SalvAiLink />
            ) : undefined
          }
          subTitle={getSubTitle(initialActiveHits.content, isAlertGoodlistingOthers)}
          className='matches-table'
          total={initialActiveHits.total}
          page={page}
          size={size}
          initialItems={initialActiveHits.content}
          body={(items) => (
            <Hits
              hits={items}
              entityType={alert.entity.entityType}
              matchSources={alert.matchSources!}
              schemaFields={customListSchema?.schemaFields ?? []}
              customListId={alert.type === ApiSanctionType.CUSTOM_SCREENING_LIST ? alert.customListId : undefined}
              screenedValue={screenedValue}
            />
          )}
          header={() => getHeader()}
          fetchPage={fetchActiveHits}
        />
      )}
      {initialFilteredHits.total > 0 && <FilteredSection initialFilteredHits={initialFilteredHits} />}
    </ScreeningMatchesContext.Provider>
  );
};

export default Matches;

interface ContextProps {
  isFirstMatchAiInsightExpanded: boolean;
  toggleFirstMatchAiInsightExpansion: () => void;
}
export const ScreeningMatchesContext = createContext<ContextProps>({
  isFirstMatchAiInsightExpanded: false,
  toggleFirstMatchAiInsightExpansion: () => {},
});
